/**
 * Background style markers — the vocabulary the design team encodes in legacy
 * background FILENAMES (`YT_blacktext_productlogo_blacklogo_graybg_&e07eacd1.png`)
 * and the single source of truth for both consumers of it:
 *
 *   1. the marker classNames App puts on the root node (`blacklogo`, `graybg`, …),
 *      which template CSS keys off;
 *   2. logo-variant resolution in libs/logo.js, which needs the qualified form
 *      (`_whitelogo` vs `_whitelogo_inverted`).
 *
 * The markers have to travel as DATA, not as a substring of the url: issue #567
 * re-hosts background bytes under content-hashed keys (`bg/<sha12>.png`), where
 * the filename says nothing at all. The v3 migration parses this table once and
 * persists the result per image; the legacy path derives it from the url it is
 * about to render. Both call `bgMarkersFromUrl`, so the two cannot drift.
 *
 * Dependency-free on purpose — the migration runs server-side and cannot import
 * libs/bg-utils.js (DOMPurify + client constants), which is why that table used
 * to exist in two copies.
 */

interface MarkerDef {
  /** Filename substring, matched with a `_`/`.`/end-of-string boundary after it. */
  substring: string;
  /** Marker name as persisted in the catalog and returned to callers. */
  marker: string;
  /**
   * Whether the marker is also a CSS class on the app root. Qualifiers are not:
   * no stylesheet consumes them, and emitting them would make the DOM differ
   * between the catalog and legacy paths.
   */
  cssClass: boolean;
}

/**
 * Order matters twice over: it is the order markers are returned in, hence the
 * className order App renders (`blacklogo whitebg showgrid`, never filename
 * order), and it keeps the qualifiers last so adding them left the existing
 * className output untouched.
 */
const MARKER_DEFS: MarkerDef[] = [
  {substring: "_blacklogo", marker: "blacklogo", cssClass: true},
  {substring: "_blacktext", marker: "blacktext", cssClass: true},
  {substring: "_productlogo", marker: "productlogo", cssClass: true},
  {substring: "_whitebg", marker: "whitebg", cssClass: true},
  {substring: "_whitelogo", marker: "whitelogo", cssClass: true},
  {substring: "_graybg", marker: "graybg", cssClass: true},
  {substring: "_blackproduct", marker: "blackproduct", cssClass: true},
  {substring: "_bottomlogoinverse", marker: "bottomlogoinverse", cssClass: true},
  {substring: "_grid", marker: "showgrid", cssClass: true},
  // Qualifiers. A `_blacklogo_inverted` file yields BOTH `blacklogo` and
  // `blacklogoinverted` (the boundary rule matches the base substring too), so
  // consumers that care about the qualified form must test it first.
  {substring: "_blacklogo_inverted", marker: "blacklogoinverted", cssClass: false},
  {substring: "_whitelogo_inverted", marker: "whitelogoinverted", cssClass: false},
  {substring: "_whitelogo_transparent", marker: "whitelogotransparent", cssClass: false},
];

/**
 * Patterns are precompiled at module load: `bgMarkersFromUrl` runs on every bg
 * url change on the legacy path, and once per image in the migration. Substrings
 * are literal `[a-z_]` so they need no regex escaping — asserted here rather
 * than assumed, so a future entry with a `.` or `+` fails loudly at load.
 */
export const BG_MARKERS = Object.freeze(
  MARKER_DEFS.map((def) => {
    if (!/^_[a-z]+(_[a-z]+)*$/.test(def.substring)) {
      throw new Error(`Marker substring "${def.substring}" must match /^_[a-z]+(_[a-z]+)*$/`);
    }
    return Object.freeze({...def, pattern: new RegExp(`${def.substring}(?=[_.]|$)`)});
  })
);

const CSS_CLASS_MARKERS: ReadonlySet<string> = new Set(
  BG_MARKERS.filter((m) => m.cssClass).map((m) => m.marker)
);

/**
 * Style markers implied by a background filename, in BG_MARKERS order.
 * The boundary rule (`_` / `.` / end) is what keeps `_gridview` from reading as
 * `_grid` and `IJ_white_inverted.png` from reading as `_whitelogo`.
 */
export function bgMarkersFromUrl(url: string | null | undefined): string[] {
  if (!url) return [];
  return BG_MARKERS.filter(({pattern}) => pattern.test(url)).map(({marker}) => marker);
}

/** The space-separated CSS classes a marker list implies (qualifiers dropped). */
export function markerClassNames(markers: readonly string[] | null | undefined): string {
  if (!markers?.length) return "";
  return markers.filter((m) => CSS_CLASS_MARKERS.has(m)).join(" ");
}
